import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OptionSelect } from "@/components/data/FieldInput";
import { PageHeader } from "@/components/data/StatusBadge";
import { actionLabel } from "@/components/data/Dialogs";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";
import { exportExcel } from "@/lib/export";

const ACTIONS = ["connexion", "deconnexion", "echec_connexion", "creation", "modification", "suppression", "validation", "annulation",
  "cloture", "reouverture", "approbation", "rejet", "paiement", "encaissement", "reglement", "import", "affectation", "reaffectation",
  "desaffectation", "desactivation", "reactivation", "modification_role", "stock_negatif"].map((a) => ({ value: a, label: actionLabel(a) }));

export default function AuditPage() {
  const { meta, can } = useAuth();
  const [f, setF] = useState({ q: "", module: null, action: null, date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const params = { ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), size: 50 };

  useEffect(() => {
    api.get("/audit", { params: { ...params, page } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, page]);

  const mods = meta.modules.map((m) => ({ value: m.key, label: m.label }));
  const modLabel = (k) => mods.find((m) => m.value === k)?.label || k;
  const doExport = async () => {
    const r = await api.get("/audit", { params: { ...params, size: 5000 } });
    exportExcel("Journal d'audit", [
      { label: "Date", raw: (x) => fmtDate(x.date) }, { label: "Utilisateur", raw: (x) => x.user_name },
      { label: "Module", raw: (x) => modLabel(x.module) }, { label: "Action", raw: (x) => actionLabel(x.action) },
      { label: "Élément", raw: (x) => x.label }, { label: "Motif", raw: (x) => x.motif || "" }], r.data.items);
  };
  const pages = Math.max(1, Math.ceil(data.total / 50));
  const set = (k, v) => { setPage(1); setF({ ...f, [k]: v }); };

  return (
    <div>
      <PageHeader title="Journal d'audit" subtitle="Traçabilité complète : qui a fait quoi, quand, et pourquoi." />
      <div className="bg-card border rounded-lg">
        <div className="p-3 sm:p-4 border-b flex flex-wrap gap-2">
          <Input className="h-9 w-full sm:w-56" placeholder="Élément ou utilisateur…" value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="audit-search-input" />
          <div className="w-full sm:w-48"><OptionSelect options={mods} value={f.module} allowAll onChange={(v) => set("module", v)} testId="audit-module-filter" /></div>
          <div className="w-full sm:w-44"><OptionSelect options={ACTIONS} value={f.action} allowAll onChange={(v) => set("action", v)} testId="audit-action-filter" /></div>
          <Input type="date" className="h-9 w-[150px]" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} />
          <Input type="date" className="h-9 w-[150px]" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} />
          {can("audit", "exporter") && <Button variant="outline" size="sm" className="ml-auto" onClick={doExport} data-testid="audit-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-muted/60 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-3 py-2.5 text-left">Date</th><th className="px-3 text-left">Utilisateur</th><th className="px-3 text-left">Module</th><th className="px-3 text-left">Action</th><th className="px-3 text-left">Élément</th><th className="px-3 text-left">Motif</th></tr>
            </thead>
            <tbody>
              {data.items.map((l) => (
                <tr key={l.id} className="border-t">
                  <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{fmtDate(l.date)}</td>
                  <td className="px-3 whitespace-nowrap">{l.user_name}</td>
                  <td className="px-3 whitespace-nowrap">{modLabel(l.module)}</td>
                  <td className="px-3 whitespace-nowrap font-semibold">{actionLabel(l.action)}</td>
                  <td className="px-3 truncate max-w-[260px]">{l.label}</td>
                  <td className="px-3 text-xs text-muted-foreground truncate max-w-[260px]">{l.motif}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between items-center p-3 border-t text-xs text-muted-foreground">
          <span>{data.total} entrée(s)</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <span>Page {page} / {pages}</span>
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= pages} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
